import logo from "assets/logo.png";


export const CHECK_QR_POSTER_SIZE = Object.freeze({
    width: 1240,
    height: 1754,
});

const POSTER_LAYOUT_SIZE = Object.freeze({ width: 2480, height: 3508 });
const FONT_FAMILY = '"Titillium Web", Arial, sans-serif';
const TEXT_COLOR = "#222B33";
const BRAND_COLOR = "#E5027D";

const loadImage = (src) => new Promise((resolve, reject) => {
    const image = new Image();

    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("QR poster image could not be loaded"));
    image.src = src;
});

const loadRemoteImage = async (url) => {
    const response = await fetch(url);

    if (!response.ok)
    {
        throw new Error("QR download failed");
    }

    const objectUrl = URL.createObjectURL(await response.blob());

    try
    {
        return await loadImage(objectUrl);
    } finally
    {
        URL.revokeObjectURL(objectUrl);
    }
};

const setFont = (context, weight, size) => {
    context.font = `${ weight } ${ size }px ${ FONT_FAMILY }`;
};

const splitText = (context, value, maxWidth) => {
    const words = String(value || "").trim().split(/\s+/).filter(Boolean);
    const lines = [];
    let line = "";

    words.forEach((word) => {
        const nextLine = line ? `${ line } ${ word }` : word;

        if (line && context.measureText(nextLine).width > maxWidth)
        {
            lines.push(line);
            line = word;
        } else
        {
            line = nextLine;
        }
    });

    if (line) lines.push(line);

    return lines.length ? lines : [ "" ];
};

const fitTitle = (context, value) => {
    for (let size = 92; size >= 64; size -= 2)
    {
        setFont(context, 600, size);
        const lines = splitText(context, value, 2000);

        if (lines.length <= 2)
        {
            return { lines, size };
        }
    }

    for (let size = 62; size >= 44; size -= 2)
    {
        setFont(context, 600, size);
        const lines = splitText(context, value, 2000);

        if (lines.length <= 3)
        {
            return { lines, size };
        }
    }

    setFont(context, 600, 44);
    return { lines: splitText(context, value, 2000), size: 44 };
};

const drawCenteredText = (context, value, y) => {
    context.fillText(value, POSTER_LAYOUT_SIZE.width / 2, y);
};

export const createCheckQrPosterCanvas = async ({ url, eventID, eventTitle }) => {
    if (!url) throw new Error("QR URL is required");

    if (document.fonts?.ready)
    {
        await document.fonts.ready;
    }

    const [ qrImage, logoImage ] = await Promise.all([
        loadRemoteImage(url),
        loadImage(logo),
    ]);
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");

    if (!context) throw new Error("Canvas is not supported");

    canvas.width = CHECK_QR_POSTER_SIZE.width;
    canvas.height = CHECK_QR_POSTER_SIZE.height;

    context.scale(
        canvas.width / POSTER_LAYOUT_SIZE.width,
        canvas.height / POSTER_LAYOUT_SIZE.height
    );
    context.fillStyle = "#FFFFFF";
    context.fillRect(0, 0, POSTER_LAYOUT_SIZE.width, POSTER_LAYOUT_SIZE.height);
    context.textAlign = "center";
    context.textBaseline = "alphabetic";

    context.fillStyle = BRAND_COLOR;
    setFont(context, 600, 180);
    drawCenteredText(context, "Clock In & Out", 650);

    context.fillStyle = TEXT_COLOR;
    const title = fitTitle(context, eventTitle || `Exhibition ${ eventID }`);
    const titleLineHeight = title.size * 1.15;
    const titleStartY = 865 - ((title.lines.length - 1) * titleLineHeight / 2);

    title.lines.forEach((line, index) => {
        drawCenteredText(context, line, titleStartY + (index * titleLineHeight));
    });

    const qrBlockSize = 1370;
    const qrSize = 1228;
    const qrBlockX = (POSTER_LAYOUT_SIZE.width - qrBlockSize) / 2;
    const qrBlockY = 1065;
    const qrX = (POSTER_LAYOUT_SIZE.width - qrSize) / 2;
    const qrY = qrBlockY + ((qrBlockSize - qrSize) / 2);

    context.fillStyle = "#FFFFFF";
    context.fillRect(qrBlockX, qrBlockY, qrBlockSize, qrBlockSize);
    context.imageSmoothingEnabled = false;
    context.drawImage(qrImage, qrX, qrY, qrSize, qrSize);
    context.imageSmoothingEnabled = true;

    context.fillStyle = TEXT_COLOR;
    setFont(context, 600, 108);
    drawCenteredText(context, "Scan the QR code with", 2825);

    const logoSize = 210;
    const brandGap = 42;
    const brandText = "ELFE";

    setFont(context, 600, 76);
    const brandTextWidth = context.measureText(brandText).width;
    const brandWidth = logoSize + brandGap + brandTextWidth;
    const brandX = (POSTER_LAYOUT_SIZE.width - brandWidth) / 2;
    const brandY = 2960;

    context.drawImage(logoImage, brandX, brandY, logoSize, logoSize);
    context.textAlign = "left";
    context.fillText(brandText, brandX + logoSize + brandGap, brandY + 137);

    return canvas;
};

export const canvasToPngBlob = (canvas) => new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
        if (blob)
        {
            resolve(blob);
        } else
        {
            reject(new Error("QR poster could not be created"));
        }
    }, "image/png");
});

export const createCheckQrPosterPdf = async (params, { autoPrint = false } = {}) => {
    const canvas = await createCheckQrPosterCanvas(params);
    const { jsPDF } = await import("jspdf");
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

    doc.addImage(canvas, "PNG", 0, 0, 210, 297);

    if (autoPrint && typeof doc.autoPrint === "function")
    {
        doc.autoPrint();
    }

    return doc;
};
